// 📄 B250 (AI_113) — LAUDO ESPECIAL / RELATÓRIO CIRCUNSTANCIADO
// Sistema: HEPATO TX RONDÔNIA
// ─────────────────────────────────────────────────────────────
// Gerador FLEXÍVEL de relatórios médicos complexos/sob medida (processo
// judicial/MP, medicamento de alto custo/CEAF, segunda opinião, perícia).
// Decisão de Leonardo (AI_113): manter flexível — o médico descreve a
// FINALIDADE em texto livre; a IA estrutura. O PISO DE SEGURANÇA (fidelidade
// aos dados, ancoragem de citação B224, caráter de APOIO) vive no backend
// (etapa `laudo-especial` do /api/analyze) — fonte única, isolada do pipeline
// dos 8 laudos estruturados e do chat de discussão.
//
// Médico-only (ato privativo — Res. CFM 2.454/2026): o botão na AbaAnaliseIA só
// aparece para médico e o backend nega não-médicos (gate B243). O médico REVISA,
// EDITA e ASSINA — é o autor e o responsável final.
// ─────────────────────────────────────────────────────────────

import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import React, { useState, useEffect, useRef } from "react";
import {
  FileText,
  Scale,
  Copy,
  Printer,
  Clock,
  RefreshCw,
  Pencil,
  Eye,
  Check,
  ShieldCheck,
} from "lucide-react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { callAnalyze } from "../../../lib/analyzeClient";
import { coletarDadosCompletos } from "../services/coletarDadosPaciente";
import { montarContextoLaudoEspecial } from "../services/montarContextoLaudoEspecial";
import { obterContextoLaudoEspecial } from "../rag";
import {
  gerarPDFTextoLivre,
  montarTextoLivreHTML,
} from "../../prescricoes/services/gerarPDF";
import { useAuth } from "../../auth/context/AuthContext";
import { ehMedico } from "../../../shared/utils/permissoesCategoria";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";
import ModalAssinatura from "../../prescricoes/components/ModalAssinatura";
import { DadosAssinaturaWidget } from "../../prescricoes/services/assinaturaCliente";
import {
  montarSeloAssinaturaDigital,
  dataHoraSeloAgora,
} from "../../prescricoes/services/seloAssinatura";
import {
  gerarCodigoValidacao,
  montarBlocoQR,
} from "../../validacoes/services/validacaoCliente";

// B289/B212 (AI_131): GO-LIVE DE PRODUÇÃO CONCLUÍDO (29/06/2026) → assinatura
// habilitada para todos os médicos (o gate `ehMedico` abaixo barra não-médicos).
// Antes ficava atrás da flag local `assinar_habilitado` (só nos navegadores de teste).
const ASSINATURA_HABILITADA = true;

interface LaudoEspecialProps {
  paciente: Paciente;
  onClose: () => void;
}

// Atalhos OPCIONAIS — só pré-preenchem uma frase inicial editável no campo de
// solicitação. Zero regra rígida: o médico apaga/ajusta à vontade (decisão de
// Leonardo: flexível). A IA decide a estrutura a partir do que o médico escrever.
const ATALHOS: { rotulo: string; texto: string }[] = [
  {
    rotulo: "Judicial / MP",
    texto:
      "Relatório médico para processo judicial / Ministério Público, solicitando [medicamento/procedimento] não disponível no SUS. Destacar o diagnóstico e estadiamento, a indicação clínica com embasamento na literatura, a ausência/insuficiência de alternativas terapêuticas disponíveis e a justificativa do pedido.",
  },
  {
    rotulo: "Alto custo / CEAF",
    texto:
      "Laudo de solicitação de medicamento do Componente Especializado da Assistência Farmacêutica (CEAF / alto custo). Apresentar diagnóstico, histórico terapêutico, indicação e justificativa clínica.",
  },
  {
    rotulo: "2ª opinião",
    texto:
      "Relatório de segunda opinião especializada sobre o caso, com avaliação crítica do diagnóstico, estadiamento e conduta, e recomendação fundamentada.",
  },
  {
    rotulo: "Circunstanciado",
    texto:
      "Relatório médico circunstanciado do caso, com história clínica, diagnóstico, exames, evolução, conduta e prognóstico.",
  },
];

export function LaudoEspecial({ paciente, onClose }: LaudoEspecialProps) {
  const { usuarioLogado } = useAuth();
  const souMedico = ehMedico(usuarioLogado);

  const [dados, setDados] = useState<any>(null);
  const [carregandoDados, setCarregandoDados] = useState(true);
  const [solicitacao, setSolicitacao] = useState("");
  const [laudo, setLaudo] = useState("");
  const [laudoOriginalIA, setLaudoOriginalIA] = useState(""); // texto cru gerado pela IA, p/ marcar textoFoiEditado
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [modoEdicao, setModoEdicao] = useState(false);
  const [salvoOk, setSalvoOk] = useState(false);
  const [truncado, setTruncado] = useState(false); // laudo cortado no teto de tokens
  // B289 (AI_128): dados do modal de assinatura digital (null = fechado).
  const [modalAssinatura, setModalAssinatura] = useState<{
    html: string;
    tipo: string;
    prescricaoId: string;
    dados: DadosAssinaturaWidget;
    nomeArquivo: string;
    codigoValidacao?: string;
    dadosValidacao?: {
      medicoNome: string;
      registro: string;
      pacienteTelefone: string;
      pacienteNascimento: string;
    };
  } | null>(null);
  // Id do doc gravado no histórico de prescrições NESTA sessão do modal. Enquanto
  // existir, novas gravações ATUALIZAM o mesmo doc (sem duplicar). Uma sessão de
  // modal = um laudo no histórico, mantido atualizado (geração/edição/export).
  const docIdRef = useRef<string | null>(null);
  // Trava de gravação EM VOO: docIdRef só é setado após o addDoc resolver. Sem
  // esta trava, exportar logo após gerar (enquanto o create do auto-save está em
  // voo) reentraria no create e DUPLICARIA o laudo no histórico clínico.
  const criandoRef = useRef(false);
  // Ficha de proveniência da IA (B177/B27, AI_114) da ÚLTIMA geração. Documento
  // médico-legal (judicial/alto custo) precisa registrar o uso de IA no
  // prontuário (Art. 4.V Res. CFM 2454/2026) + auditabilidade. Vive num ref para
  // persistir entre geração e export sem nova chamada à IA.
  const provenienciaRef = useRef<Record<string, unknown> | null>(null);

  // Carregar dados do paciente ao abrir
  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregandoDados(true);
      try {
        const d = await coletarDadosCompletos(paciente);
        if (ativo) setDados(d);
      } catch (e) {
        console.error("Erro ao coletar dados do paciente:", e);
        if (ativo) setErro("Falha ao carregar os dados do paciente.");
      } finally {
        if (ativo) setCarregandoDados(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [paciente]);

  const gerar = async () => {
    if (!solicitacao.trim() || loading || !dados || !souMedico) return;
    setLoading(true);
    setErro("");
    try {
      const contextoClinico = montarContextoLaudoEspecial(dados);

      // RAG: busca padrão (dados do paciente) + busca extra guiada pela
      // SOLICITAÇÃO do médico, p/ ancorar as citações que ele pede (B224).
      // Falha não bloqueia a geração.
      let contextoGuidelines = "";
      try {
        contextoGuidelines = await obterContextoLaudoEspecial(
          dados,
          solicitacao.trim()
        );
      } catch (e) {
        console.warn("⚠️ RAG indisponível para o laudo especial");
      }

      const resp = await callAnalyze<{
        laudo?: string;
        erro?: string;
        truncado?: boolean;
        _proveniencia?: Record<string, unknown>;
      }>({
        etapa: "laudo-especial",
        contextoClinico,
        solicitacao: solicitacao.trim(),
        contextoGuidelines,
        useThinking: true,
      });

      if (resp.laudo) {
        setLaudo(resp.laudo);
        setLaudoOriginalIA(resp.laudo);
        provenienciaRef.current = resp._proveniencia || null;
        setModoEdicao(false);
        setTruncado(!!resp.truncado);
        // 🔒 Salva NA GERAÇÃO (não só no export): um laudo bom nunca se perde,
        // mesmo se a página rolar ou o médico fechar sem exportar.
        persistir(resp.laudo, resp.laudo);
      } else {
        setErro(
          "Não foi possível gerar o relatório agora. Tente novamente em instantes."
        );
        console.warn("⚠️ [laudo-especial] resposta:", resp.erro);
      }
    } catch (e) {
      console.error("Erro ao gerar laudo especial:", e);
      setErro("Erro ao gerar o relatório. Verifique a conexão e tente de novo.");
    }
    setLoading(false);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(laudo);
    } catch {
      // silencioso — alguns navegadores bloqueiam clipboard sem gesto direto
    }
  };

  const TITULO_DOC = "Relatório Médico";

  const configPDF = () => ({
    nomeAmbulatorio: "Ambulatório de Transplante Hepático de Rondônia",
    nomeMedico: usuarioLogado?.nome || "Médico",
    crm: usuarioLogado?.registroProfissional
      ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
      : "",
    endereco: "Av. Gov. Jorge Teixeira, 3766",
    cidade: "Porto Velho - RO",
  });

  // B250 (AI_113) — grava no histórico de prescrições (pedido de Leonardo).
  // Espelha o save do TextoLivre: coleção `prescricoes`, tipo "texto", subtipo
  // "laudo_medico" (ato privativo do médico — B92/B88). CRIA no 1º save e
  // ATUALIZA o MESMO doc depois (sem duplicar). Chamado NA GERAÇÃO (nunca perde
  // o laudo) e de novo no export (caso o médico tenha editado o texto).
  const persistir = async (texto: string, originalIA: string) => {
    if (!texto.trim() || !souMedico) return;
    const foiEditado = originalIA !== "" && originalIA !== texto;
    try {
      if (docIdRef.current) {
        await updateDoc(doc(db, "prescricoes", docIdRef.current), {
          "conteudo.texto": texto,
          // mantém a solicitação gravada coerente com o laudo após regerar.
          "_ia.solicitacao": solicitacao.trim(),
          "_ia.textoFoiEditado": foiEditado,
          // proveniência da última geração (atualiza ao regerar; idempotente no export).
          "_ia._proveniencia": provenienciaRef.current || null,
        });
      } else {
        // Trava anti-duplicação: se um create já está em voo, não cria outro
        // (evita laudo DUPLICADO ao exportar logo após gerar). finally interno
        // libera só para a chamada que travou (não afeta o early-return acima).
        if (criandoRef.current) return;
        criandoRef.current = true;
        try {
          const ref = await addDoc(collection(db, "prescricoes"), {
            pacienteId: paciente.id,
            tipo: "texto",
            // B92/B88: laudo é ato privativo do médico (modal já é médico-only).
            subtipo: "laudo_medico",
            titulo: `${TITULO_DOC} - ${paciente.nome}`,
            conteudo: { texto },
            config: configPDF(),
            criadoEm: serverTimestamp(),
            criadoPor: usuarioLogado?.id || "",
            criadoPorNome: usuarioLogado?.nome || "",
            _ia: {
              usouIA: true,
              origem: "laudo_especial", // distingue do TextoLivre no histórico
              solicitacao: solicitacao.trim(),
              textoFoiEditado: foiEditado,
              // B177/B27 (AI_114): ficha de proveniência da IA no documento.
              _proveniencia: provenienciaRef.current || null,
            },
          });
          docIdRef.current = ref.id;
        } finally {
          criandoRef.current = false;
        }
      }
      setSalvoOk(true);
    } catch (error) {
      console.error("Erro ao salvar laudo no histórico:", error);
    }
  };

  const exportarPDF = async () => {
    if (!laudo.trim()) return;
    // rodapeSimplificado: rodapé só "Cidade, Data". O nome/CRM do médico já vêm
    // no CABEÇALHO do PDF (e o laudo encerra sem bloco de assinatura — a
    // assinatura será digital, B212). Pedido de Leonardo.
    gerarPDFTextoLivre(paciente, TITULO_DOC, laudo, configPDF(), {
      rodapeSimplificado: true,
    });
    // Atualiza o histórico com o texto atual (pode ter sido editado após gerar).
    await persistir(laudo, laudoOriginalIA);
  };

  // B289 (AI_128): assina digitalmente o Laudo Especial (médico-only + flag).
  // Espelha o handleAssinarTextoLivre: monta o MESMO HTML do export (rodapé
  // simplificado) + selo ICP-Brasil, garante o doc persistido e captura o id para
  // o broker validar a autoria (criadoPor==uid), e abre o ModalAssinatura. Reusa
  // o doc do auto-save (docIdRef) — NÃO cria um novo (não duplica no histórico).
  const handleAssinarLaudoEspecial = async () => {
    if (!laudo.trim() || !souMedico) return;
    // Garante o texto atual gravado e captura o id do doc já existente.
    await persistir(laudo, laudoOriginalIA);
    const prescricaoId = docIdRef.current;
    if (!prescricaoId) {
      alert(
        "Não foi possível preparar o documento para assinatura. Tente novamente em instantes."
      );
      return;
    }
    const registro = usuarioLogado?.registroProfissional
      ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
      : "";
    // B290 — QR de validação no rodapé (ao lado do selo grande ICP-Brasil).
    const codigo = gerarCodigoValidacao();
    const selo =
      montarSeloAssinaturaDigital({
        nome: usuarioLogado?.nome || "",
        registro,
        data: dataHoraSeloAgora(),
      }) + montarBlocoQR(codigo);
    const html = montarTextoLivreHTML(
      paciente,
      TITULO_DOC,
      laudo,
      configPDF(),
      { rodapeSimplificado: true },
      selo
    );
    setModalAssinatura({
      html,
      tipo: "texto",
      prescricaoId,
      dados: {
        name: usuarioLogado?.nome || "",
        reason: TITULO_DOC,
        location: configPDF().cidade || "",
      },
      nomeArquivo: `${TITULO_DOC}-${paciente.nome}`.replace(/\s+/g, "_"),
      codigoValidacao: codigo,
      dadosValidacao: {
        medicoNome: usuarioLogado?.nome || "",
        registro,
        pacienteTelefone: telefoneVigente(paciente),
        pacienteNascimento: paciente.dataNascimento || "",
      },
    });
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: "90%",
          maxWidth: "860px",
          height: "88vh",
          backgroundColor: "white",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            backgroundColor: theme.primary[600],
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 600,
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Scale size={18} /> Laudo Especial / Relatório
            </h2>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "14px",
                opacity: 0.9,
                fontFamily: theme.fonts.sans,
              }}
            >
              {paciente.nome}
              {dados?.idade ? ` • ${dados.idade} anos` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "24px",
              cursor: "pointer",
              padding: "4px 8px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Corpo */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 24px",
            backgroundColor: "#f9fafb",
          }}
        >
          {carregandoDados ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                color: theme.gray[500],
              }}
            >
              <Clock size={32} style={{ marginBottom: "12px" }} />
              <p>Carregando dados do paciente...</p>
            </div>
          ) : !souMedico ? (
            <div
              style={{
                padding: "24px",
                color: theme.gray[600],
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              A geração de laudos é ato privativo do médico (Res. CFM 2.454/2026).
            </div>
          ) : (
            <>
              {/* Campo de solicitação */}
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: theme.gray[700],
                  marginBottom: "6px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Descreva a finalidade e o que o relatório precisa conter
              </label>

              {/* Atalhos opcionais */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                {ATALHOS.map((a) => (
                  <button
                    key={a.rotulo}
                    onClick={() => setSolicitacao(a.texto)}
                    title="Preenche um modelo inicial — edite à vontade"
                    style={{
                      padding: "5px 12px",
                      fontSize: "12px",
                      backgroundColor: theme.gray[100],
                      border: "1px solid " + theme.gray[200],
                      borderRadius: "16px",
                      cursor: "pointer",
                      color: theme.gray[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {a.rotulo}
                  </button>
                ))}
              </div>

              <textarea
                value={solicitacao}
                onChange={(e) => setSolicitacao(e.target.value)}
                placeholder="Ex.: Relatório para processo judicial solicitando atezolizumabe + bevacizumabe para CHC avançado fora dos critérios de ressecção/transplante, destacando a indicação, a literatura de suporte (IMbrave150) e a ausência de alternativa no SUS."
                disabled={loading}
                style={{
                  width: "100%",
                  minHeight: "90px",
                  maxHeight: "200px",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: "1px solid " + theme.gray[300],
                  fontFamily: theme.fonts.sans,
                  fontSize: "14px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />

              <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                <button
                  onClick={gerar}
                  disabled={loading || !solicitacao.trim()}
                  style={{
                    padding: "10px 20px",
                    backgroundColor:
                      loading || !solicitacao.trim()
                        ? theme.gray[300]
                        : theme.primary[600],
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor:
                      loading || !solicitacao.trim()
                        ? "not-allowed"
                        : "pointer",
                    fontFamily: theme.fonts.sans,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {loading ? (
                    <>
                      <Clock size={15} /> Gerando relatório…
                    </>
                  ) : (
                    <>
                      <FileText size={15} /> {laudo ? "Gerar novamente" : "Gerar relatório"}
                    </>
                  )}
                </button>
              </div>

              {loading && (
                <p
                  style={{
                    marginTop: "10px",
                    fontSize: "12px",
                    color: theme.gray[500],
                    fontStyle: "italic",
                  }}
                >
                  Relatórios complexos podem levar de 30 a 90 segundos.
                </p>
              )}

              {erro && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px 14px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    color: "#b91c1c",
                    fontSize: "13px",
                  }}
                >
                  {erro}
                </div>
              )}

              {/* Resultado */}
              {laudo && truncado && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "10px 14px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    color: "#b91c1c",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  ⚠️ O relatório pode ter sido CORTADO por tamanho. Revise o
                  final e, se faltar conteúdo, gere novamente ou complete
                  manualmente antes de usar.
                </div>
              )}
              {laudo && (
                <div style={{ marginTop: "18px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: theme.gray[700],
                      }}
                    >
                      Relatório gerado
                    </span>
                    <button
                      onClick={() => setModoEdicao((v) => !v)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "5px 10px",
                        fontSize: "12px",
                        backgroundColor: "white",
                        border: "1px solid " + theme.gray[300],
                        borderRadius: "8px",
                        cursor: "pointer",
                        color: theme.gray[700],
                      }}
                    >
                      {modoEdicao ? (
                        <>
                          <Eye size={13} /> Ver formatado
                        </>
                      ) : (
                        <>
                          <Pencil size={13} /> Editar texto
                        </>
                      )}
                    </button>
                  </div>

                  {modoEdicao ? (
                    <textarea
                      value={laudo}
                      onChange={(e) => {
                        setLaudo(e.target.value);
                        setSalvoOk(false); // edição → "exporte para atualizar o histórico"
                      }}
                      style={{
                        width: "100%",
                        minHeight: "320px",
                        padding: "14px",
                        borderRadius: "10px",
                        border: "1px solid " + theme.gray[300],
                        fontFamily: theme.fonts.sans,
                        fontSize: "13px",
                        lineHeight: "1.6",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        backgroundColor: "white",
                        border: "1px solid " + theme.gray[200],
                        borderRadius: "10px",
                        padding: "16px 18px",
                      }}
                    >
                      <TextoAnaliseMarkdown texto={laudo} />
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "11px",
                      color: theme.gray[500],
                      fontStyle: "italic",
                    }}
                  >
                    ⚠️ Documento gerado com apoio de IA. Revise, ajuste e assine —
                    você é o autor e o responsável final.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Rodapé com ações do resultado */}
        {laudo && souMedico && (
          <div
            style={{
              padding: "12px 24px",
              borderTop: "1px solid " + theme.gray[200],
              backgroundColor: "white",
              display: "flex",
              gap: "10px",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {salvoOk && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "12px",
                  color: theme.primary[700],
                  marginRight: "auto",
                }}
              >
                <Check size={14} /> Salvo no histórico de prescrições
              </span>
            )}
            <button
              onClick={copiar}
              style={botaoSecundario}
            >
              <Copy size={14} /> Copiar
            </button>
            <button
              onClick={gerar}
              disabled={loading}
              style={botaoSecundario}
            >
              <RefreshCw size={14} /> Regerar
            </button>
            <button
              onClick={exportarPDF}
              style={{
                ...botaoSecundario,
                backgroundColor: theme.primary[600],
                color: "white",
                border: "none",
              }}
            >
              <Printer size={14} /> Exportar PDF
            </button>
            {ASSINATURA_HABILITADA && (
              <button
                onClick={handleAssinarLaudoEspecial}
                style={{
                  ...botaoSecundario,
                  backgroundColor: theme.primary[700],
                  color: "white",
                  border: "none",
                }}
              >
                <ShieldCheck size={14} /> Assinar digitalmente
              </button>
            )}
          </div>
        )}
      </div>

      {modalAssinatura && (
        <ModalAssinatura
          aberto={true}
          onFechar={() => setModalAssinatura(null)}
          html={modalAssinatura.html}
          tipo={modalAssinatura.tipo}
          prescricaoId={modalAssinatura.prescricaoId}
          dados={modalAssinatura.dados}
          nomeArquivo={modalAssinatura.nomeArquivo}
          codigoValidacao={modalAssinatura.codigoValidacao}
          dadosValidacao={modalAssinatura.dadosValidacao}
        />
      )}
    </div>
  );
}

const botaoSecundario: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "9px 16px",
  fontSize: "13px",
  fontWeight: 600,
  backgroundColor: "white",
  border: "1px solid " + theme.gray[300],
  borderRadius: "10px",
  cursor: "pointer",
  color: theme.gray[700],
  fontFamily: theme.fonts.sans,
};
